/**
 * Appipa Calculator Manager
 * Manages the lifecycle of the Appipa custom pricing calculator
 * Note: This works with the auto-rendered calculator from process-product-page.js
 */

/**
 * Hides the calculator that was auto-rendered by ProcessProductPage
 * Should be called immediately after calculator is detected
 */
function hideCalculator() {
  const calculatorContainer = document.querySelector('.cpc-calculator-target') || 
                              document.getElementById('calculator-block');
  
  const calculator = document.getElementById("calculator");
  
  if (calculatorContainer) {
    calculatorContainer.style.display = 'none';
    calculatorContainer.classList.add('appipa-calculator-hidden');
  }
  
  if (calculator) {
    calculator.style.display = 'none';
    calculator.classList.add('appipa-calculator-hidden');
  }

  // Stop watching for new inputs when calculator is hidden
  stopCalculatorInputObserver();
  
  // Stop watching price ticker when calculator is hidden
  stopPriceTickerObserver();

  // Show product form elements when calculator is hidden
  updateProductFormElementsVisibility();
}

// MutationObserver to watch for new inputs being added to calculator
let calculatorInputObserver = null;

/**
 * Sets up a MutationObserver to watch for new number inputs in the calculator
 * and transform them automatically when they're added
 */
function setupCalculatorInputObserver() {
  // Disconnect existing observer if any
  if (calculatorInputObserver) {
    calculatorInputObserver.disconnect();
    calculatorInputObserver = null;
  }

  const calculator = document.getElementById("calculator");
  if (!calculator) {
    return;
  }

  // Watch for new inputs being added to the calculator
  calculatorInputObserver = new MutationObserver((mutations) => {
    let shouldTransform = false;
    let foundInputs = [];
    let foundSelects = false;

    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        for (const addedNode of mutation.addedNodes) {
          if (addedNode instanceof HTMLElement) {
            // Check if the added node is a number input or contains one
            if (addedNode instanceof HTMLInputElement && addedNode.type === 'number' && 
                !addedNode.hasAttribute('data-appipa-transformed') &&
                !addedNode.hasAttribute('data-appipa-skip-transform')) {
              foundInputs.push(addedNode);
              shouldTransform = true;
            }
            
            // Check if the added node is a select or contains one
            if (addedNode instanceof HTMLSelectElement || addedNode.querySelector('select')) {
              foundSelects = true;
            }
            
            // Check if any child inputs were added
            const newInputs = addedNode.querySelectorAll?.('input[type="number"]:not([data-appipa-transformed="true"]):not([data-appipa-skip-transform="true"])');
            if (newInputs && newInputs.length > 0) {
              foundInputs.push(...Array.from(newInputs));
              shouldTransform = true;
            }
            
            // Check if any child selects were added
            const newSelects = addedNode.querySelectorAll?.('select');
            if (newSelects && newSelects.length > 0) {
              foundSelects = true;
            }
          }
        }
      }
    }

    // Transform inputs if any were added
    if (shouldTransform) {
      // Use a small delay to ensure Appipa has finished setting up the input
      setTimeout(() => {
        transformCalculatorNumberInputs();
      }, 50);
    }
    
    // Hide single-option selects if any were added
    if (foundSelects) {
      setTimeout(() => {
        hideSingleOptionSelects();
      }, 50);
    }
  });

  // Start observing the calculator for new inputs
  calculatorInputObserver.observe(calculator, {
    childList: true,
    subtree: true
  });
}

/**
 * Stops watching for new calculator inputs
 */
function stopCalculatorInputObserver() {
  if (calculatorInputObserver) {
    calculatorInputObserver.disconnect();
    calculatorInputObserver = null;
  }
}

/**
 * Transforms number inputs in the calculator to match quantity selector styling
 * Preserves all Appipa attributes and functionality while applying visual styling
 */
function transformCalculatorNumberInputs() {
  const calculator = document.getElementById("calculator");
  if (!calculator) {
    return;
  }
  
  // Find all number inputs in the calculator that aren't already wrapped or explicitly excluded
  const numberInputs = calculator.querySelectorAll('input[type="number"]:not([data-appipa-transformed="true"]):not([data-appipa-skip-transform="true"])');
  
  
  if (numberInputs.length === 0) {
    return;
  }

  // SVG icons matching theme icons
  const minusIcon = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.75 7H11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/></svg>';
  const plusIcon = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path class="vertical" d="M2.75 7H11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/><path class="horizontal" d="M7 2.75L7 11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/></svg>';

  numberInputs.forEach((input, inputIndex) => {
    // Skip if already transformed
    if (input.dataset.appipaTransformed === 'true') {
      return;
    }
    
    // Skip if input is already inside a quantity-selector (already wrapped)
    if (input.closest('.quantity-selector')) {
      return;
    }
    
    // Skip if Appipa has explicitly marked this input to skip transformation
    if (input.dataset.appipaSkipTransform === 'true') {
      return;
    }

    // Preserve ALL original attributes - capture everything Appipa might need
    const originalAttributes = {
      // Standard attributes
      value: input.value,
      min: input.min,
      max: input.max,
      step: input.step || '1',
      name: input.name,
      id: input.id, // Preserve original ID - don't generate new ones
      className: input.className,
      required: input.required,
      disabled: input.disabled,
      readonly: input.readOnly,
      placeholder: input.placeholder,
      pattern: input.pattern,
      autocomplete: input.autocomplete,
      form: input.getAttribute('form') || null, // Get the form attribute value, not the form element
      formaction: input.getAttribute('formaction'),
      formmethod: input.getAttribute('formmethod'),
      formnovalidate: input.hasAttribute('formnovalidate'),
      formtarget: input.getAttribute('formtarget'),
      // Data attributes - preserve all
      dataAttributes: {},
      // Aria attributes - preserve all
      ariaAttributes: {},
      // Other attributes Appipa might use
      otherAttributes: {}
    };

    // Capture all attributes - preserve everything Appipa needs
    Array.from(input.attributes).forEach(attr => {
      if (attr.name.startsWith('data-')) {
        originalAttributes.dataAttributes[attr.name] = attr.value;
      } else if (attr.name.startsWith('aria-')) {
        originalAttributes.ariaAttributes[attr.name] = attr.value;
      } else if (attr.name.startsWith('on')) {
        // Preserve all inline event handlers (onkeyup, onchange, onblur, etc.)
        originalAttributes.otherAttributes[attr.name] = attr.value;
      } else if (!['type', 'value', 'min', 'max', 'step', 'name', 'id', 'class', 'required', 'disabled', 'readonly', 'placeholder', 'pattern', 'autocomplete', 'form', 'formaction', 'formmethod', 'formnovalidate', 'formtarget', 'style'].includes(attr.name)) {
        originalAttributes.otherAttributes[attr.name] = attr.value;
      }
    });

    // Get parent element and verify input is still a child
    const parent = input.parentElement;
    if (!parent) return;
    
    // Verify the input is actually a child of the parent before proceeding
    if (!parent.contains(input)) return;
    
    // Find the form element that contains this input (if any)
    // This is important for Appipa's cart handler to detect the input
    const formElement = input.closest('form') || (/** @type {HTMLInputElement} */ (input)).form;
    const formId = formElement?.id || null;

    // Create quantity selector wrapper
    const quantitySelector = document.createElement('div');
    quantitySelector.className = 'quantity-selector';
    // Store reference to input ID for restoration (use original ID if it exists)
    quantitySelector.dataset.appipaInputId = originalAttributes.id || `appipa-input-${Date.now()}-${Math.random()}`;
    
    // Create minus button
    const minusButton = document.createElement('button');
    minusButton.type = 'button';
    minusButton.className = 'button quantity-minus button-unstyled';
    minusButton.setAttribute('aria-label', 'Decrease');
    const minusButtonIcon = document.createElement('span');
    minusButtonIcon.className = 'svg-wrapper icon-plus';
    minusButtonIcon.innerHTML = minusIcon;
    const minusButtonHidden = document.createElement('span');
    minusButtonHidden.className = 'visually-hidden';
    minusButtonHidden.textContent = 'Decrease';
    minusButton.appendChild(minusButtonHidden);
    minusButton.appendChild(minusButtonIcon);

    // Create plus button
    const plusButton = document.createElement('button');
    plusButton.type = 'button';
    plusButton.className = 'button quantity-plus button-unstyled';
    plusButton.setAttribute('aria-label', 'Increase');
    const plusButtonIcon = document.createElement('span');
    plusButtonIcon.className = 'svg-wrapper icon-plus';
    plusButtonIcon.innerHTML = plusIcon;
    const plusButtonHidden = document.createElement('span');
    plusButtonHidden.className = 'visually-hidden';
    plusButtonHidden.textContent = 'Increase';
    plusButton.appendChild(plusButtonHidden);
    plusButton.appendChild(plusButtonIcon);

    // Update input - preserve all original classes and attributes, just add our styling class
    // Append our class instead of replacing - this preserves Appipa's classes
    if (originalAttributes.className) {
      input.className = `${originalAttributes.className} appipa-number-input`.trim();
    } else {
      input.className = 'appipa-number-input';
    }
    
    // Mark as transformed but preserve all other data attributes
    input.dataset.appipaTransformed = 'true';
    
    // Restore all preserved attributes (they should already be there, but ensure they're intact)
    // ID is critical - preserve original, don't generate new one
    if (originalAttributes.id) {
      input.id = originalAttributes.id;
    }
    
    // Restore all data attributes
    Object.entries(originalAttributes.dataAttributes).forEach(([name, value]) => {
      if (name !== 'data-appipa-transformed') {
        input.setAttribute(name, value);
      }
    });
    
    // Restore all aria attributes
    Object.entries(originalAttributes.ariaAttributes).forEach(([name, value]) => {
      input.setAttribute(name, value);
    });
    
    // Restore other attributes
    Object.entries(originalAttributes.otherAttributes).forEach(([name, value]) => {
      input.setAttribute(name, value);
    });
    
    // Restore form-related attributes
    // Only set form attribute if it was originally present (don't add it if it wasn't there)
    // This is critical - Appipa's cart handler needs to detect inputs, and adding
    // a form attribute when it wasn't there can break detection
    if (originalAttributes.form) {
      input.setAttribute('form', originalAttributes.form);
    } else {
      // Remove form attribute if it wasn't originally present
      // Don't add it even if the input is associated with a form via DOM hierarchy
      // Appipa should be able to find it by name/data attributes regardless
      input.removeAttribute('form');
    }
    if (originalAttributes.formaction) {
      input.setAttribute('formaction', originalAttributes.formaction);
    }
    if (originalAttributes.formmethod) {
      input.setAttribute('formmethod', originalAttributes.formmethod);
    }
    if (originalAttributes.formnovalidate) {
      input.setAttribute('formnovalidate', '');
    }
    if (originalAttributes.formtarget) {
      input.setAttribute('formtarget', originalAttributes.formtarget);
    }

    // Helper to determine if value should be treated as integer or float
    const parseValue = (val) => {
      const step = parseFloat(originalAttributes.step) || 1;
      // If step is a whole number, treat as integer; otherwise as float
      if (step % 1 === 0) {
        return parseInt(val, 10) || 0;
      }
      return parseFloat(val) || 0;
    };

    // Add increment/decrement functionality
    const incrementValue = () => {
      const currentValue = parseValue(input.value) || parseValue(originalAttributes.min) || 0;
      const step = parseFloat(originalAttributes.step) || 1;
      const max = originalAttributes.max ? parseFloat(originalAttributes.max) : Infinity;
      const newValue = Math.min(max, currentValue + step);
      
      // Format value based on step (preserve decimals if step has decimals)
      const formattedValue = step % 1 === 0 ? newValue.toString() : newValue.toFixed(step.toString().split('.')[1]?.length || 0);
      
      // Update value
      input.value = formattedValue;
      
      // Trigger Appipa's inline event handlers if they exist
      // These are preserved as attributes and will fire when we dispatch events
      // But we also need to ensure the value change triggers them
      const changeEvent = new Event('change', { bubbles: true, cancelable: true });
      const inputEvent = new InputEvent('input', { bubbles: true, cancelable: true });
      const keyupEvent = new KeyboardEvent('keyup', { bubbles: true, cancelable: true, key: 'ArrowUp' });
      
      // Dispatch events - Appipa's inline handlers (onchange, onkeyup, oninput) will fire automatically
      input.dispatchEvent(inputEvent);
      input.dispatchEvent(changeEvent);
      input.dispatchEvent(keyupEvent);
      
      updateButtonStates();
    };

    const decrementValue = () => {
      const currentValue = parseValue(input.value) || parseValue(originalAttributes.min) || 0;
      const step = parseFloat(originalAttributes.step) || 1;
      const min = originalAttributes.min ? parseFloat(originalAttributes.min) : 0;
      const newValue = Math.max(min, currentValue - step);
      
      // Format value based on step (preserve decimals if step has decimals)
      const formattedValue = step % 1 === 0 ? newValue.toString() : newValue.toFixed(step.toString().split('.')[1]?.length || 0);
      
      // Update value
      input.value = formattedValue;
      
      // Trigger Appipa's inline event handlers if they exist
      const changeEvent = new Event('change', { bubbles: true, cancelable: true });
      const inputEvent = new InputEvent('input', { bubbles: true, cancelable: true });
      const keyupEvent = new KeyboardEvent('keyup', { bubbles: true, cancelable: true, key: 'ArrowDown' });
      
      // Dispatch events - Appipa's inline handlers (onchange, onkeyup, oninput) will fire automatically
      input.dispatchEvent(inputEvent);
      input.dispatchEvent(changeEvent);
      input.dispatchEvent(keyupEvent);
      
      updateButtonStates();
    };

    const updateButtonStates = () => {
      const currentValue = parseValue(input.value) || parseValue(originalAttributes.min) || 0;
      const min = originalAttributes.min ? parseFloat(originalAttributes.min) : 0;
      const max = originalAttributes.max ? parseFloat(originalAttributes.max) : Infinity;
      
      minusButton.disabled = currentValue <= min || originalAttributes.disabled;
      plusButton.disabled = currentValue >= max || originalAttributes.disabled;
    };

    // Add event listeners
    plusButton.addEventListener('click', (e) => {
      e.preventDefault();
      incrementValue();
    });

    minusButton.addEventListener('click', (e) => {
      e.preventDefault();
      decrementValue();
    });

    input.addEventListener('change', updateButtonStates);
    input.addEventListener('input', updateButtonStates);
    
    // Helper function to recreate buttons if needed
    const recreateButtons = () => {
      const newMinusButton = document.createElement('button');
      newMinusButton.type = 'button';
      newMinusButton.className = 'button quantity-minus button-unstyled';
      newMinusButton.setAttribute('aria-label', 'Decrease');
      const minusButtonIcon = document.createElement('span');
      minusButtonIcon.className = 'svg-wrapper icon-plus';
      minusButtonIcon.innerHTML = minusIcon;
      const minusButtonHidden = document.createElement('span');
      minusButtonHidden.className = 'visually-hidden';
      minusButtonHidden.textContent = 'Decrease';
      newMinusButton.appendChild(minusButtonHidden);
      newMinusButton.appendChild(minusButtonIcon);
      
      const newPlusButton = document.createElement('button');
      newPlusButton.type = 'button';
      newPlusButton.className = 'button quantity-plus button-unstyled';
      newPlusButton.setAttribute('aria-label', 'Increase');
      const plusButtonIcon = document.createElement('span');
      plusButtonIcon.className = 'svg-wrapper icon-plus';
      plusButtonIcon.innerHTML = plusIcon;
      const plusButtonHidden = document.createElement('span');
      plusButtonHidden.className = 'visually-hidden';
      plusButtonHidden.textContent = 'Increase';
      newPlusButton.appendChild(plusButtonHidden);
      newPlusButton.appendChild(plusButtonIcon);
      
      // Re-attach event listeners
      newPlusButton.addEventListener('click', (e) => {
        e.preventDefault();
        incrementValue();
      });
      newMinusButton.addEventListener('click', (e) => {
        e.preventDefault();
        decrementValue();
      });
      
      return { minus: newMinusButton, plus: newPlusButton };
    };
    
    // Function to restore wrapper structure
    const restoreWrapperStructure = () => {
      // Find the quantity selector wrapper
      const wrapper = input.closest('.quantity-selector') || 
                      document.querySelector(`.quantity-selector[data-appipa-input-id="${quantitySelector.dataset.appipaInputId}"]`);
      
      if (!wrapper) {
        // Wrapper doesn't exist, recreate it
        const currentParent = input.parentElement;
        if (currentParent) {
          const newWrapper = document.createElement('div');
          newWrapper.className = 'quantity-selector';
          newWrapper.dataset.appipaInputId = quantitySelector.dataset.appipaInputId;
          
          const buttons = recreateButtons();
          newWrapper.appendChild(buttons.minus);
          newWrapper.appendChild(input);
          newWrapper.appendChild(buttons.plus);
          
          const nextSibling = input.nextSibling;
          if (nextSibling) {
            currentParent.insertBefore(newWrapper, nextSibling);
          } else {
            currentParent.appendChild(newWrapper);
          }
          
          updateButtonStates();
        }
        return;
      }
      
      // Wrapper exists - check if it has both buttons
      const existingButtons = wrapper.querySelectorAll('button.quantity-minus, button.quantity-plus');
      const hasMinus = wrapper.querySelector('button.quantity-minus');
      const hasPlus = wrapper.querySelector('button.quantity-plus');
      
      // If input is not in wrapper, move it back
      if (!wrapper.contains(input)) {
        if (hasPlus) {
          wrapper.insertBefore(input, hasPlus);
        } else if (hasMinus) {
          wrapper.insertBefore(input, hasMinus.nextSibling);
        } else {
          // No buttons, recreate structure
          while (wrapper.firstChild) {
            wrapper.removeChild(wrapper.firstChild);
          }
          const buttons = recreateButtons();
          wrapper.appendChild(buttons.minus);
          wrapper.appendChild(input);
          wrapper.appendChild(buttons.plus);
        }
      }
      
      // If buttons are missing, restore them
      if (!hasMinus || !hasPlus) {
        // Remove all children and recreate
        const inputValue = input.value;
        const inputWasInWrapper = wrapper.contains(input);
        
        while (wrapper.firstChild) {
          wrapper.removeChild(wrapper.firstChild);
        }
        
        const buttons = recreateButtons();
        wrapper.appendChild(buttons.minus);
        if (inputWasInWrapper) {
          wrapper.appendChild(input);
        } else {
          // Input was moved out, need to get it back
          const currentParent = input.parentElement;
          if (currentParent && currentParent !== wrapper) {
            wrapper.appendChild(input);
          }
        }
        wrapper.appendChild(buttons.plus);
        
        // Restore input value
        if (inputValue) {
          input.value = inputValue;
        }
      }
      
      updateButtonStates();
    };
    
    // Flag to prevent infinite loops during restoration
    let isRestoring = false;
    let restoreTimeout = null;
    
    // Debounced restoration function
    const debouncedRestore = () => {
      if (isRestoring) return; // Already restoring, skip
      
      if (restoreTimeout) {
        clearTimeout(restoreTimeout);
      }
      
      restoreTimeout = setTimeout(() => {
        isRestoring = true;
        restoreWrapperStructure();
        // Reset flag after a short delay
        setTimeout(() => {
          isRestoring = false;
        }, 100);
      }, 50); // Small delay to batch multiple mutations
    };
    
    // Use MutationObserver to watch for button removal - more defensive to avoid conflicts
    const observer = new MutationObserver((mutations) => {
      // Skip if we're currently restoring to avoid infinite loops
      if (isRestoring) return;
      
      // Check if any removed nodes were buttons
      let buttonsRemoved = false;
      let inputMoved = false;
      let shouldRestore = false;
      
      for (const mutation of mutations) {
        // Only process if mutation is in our scope
        const target = mutation.target;
        if (!target || (!target.contains(quantitySelector) && target !== quantitySelector && !quantitySelector.contains(target))) {
          // Mutation is outside our scope, skip
          continue;
        }
        
        if (mutation.type === 'childList') {
          // Check if buttons were removed
          for (const removedNode of mutation.removedNodes) {
            if (removedNode instanceof HTMLElement) {
              // Check if removed node is one of our buttons
              if (removedNode === minusButton || removedNode === plusButton) {
                buttonsRemoved = true;
                shouldRestore = true;
              }
              // Check if removed node contains our buttons
              else if (removedNode.querySelector && (
                  removedNode.querySelector('button.quantity-minus') === minusButton ||
                  removedNode.querySelector('button.quantity-plus') === plusButton
                )) {
                buttonsRemoved = true;
                shouldRestore = true;
              }
              
              // Check if the removed node was the quantity selector wrapper
              if (removedNode === quantitySelector || 
                  (removedNode.classList.contains('quantity-selector') && 
                   removedNode.dataset.appipaInputId === quantitySelector.dataset.appipaInputId)) {
                inputMoved = true;
                shouldRestore = true;
              }
            }
          }
          
          // Check if input was moved out of wrapper (but only if we haven't already detected it)
          if (!inputMoved && input.isConnected) {
            const wrapper = input.closest('.quantity-selector');
            if (!wrapper || wrapper.dataset.appipaInputId !== quantitySelector.dataset.appipaInputId) {
              inputMoved = true;
              shouldRestore = true;
            }
          }
        }
      }
      
      // Only restore if we detected a real issue and we're not already restoring
      if (shouldRestore && !isRestoring) {
        debouncedRestore();
      }
    });
    
    // Start observing - be more selective about what we observe
    // Only observe the wrapper itself and its immediate parent to reduce conflicts
    observer.observe(quantitySelector, {
      childList: true,
      attributes: false, // Don't watch attributes to reduce noise
      subtree: false // Only direct children
    });
    
    // Also observe parent for wrapper removal, but only direct parent
    if (quantitySelector.parentElement) {
      observer.observe(quantitySelector.parentElement, {
        childList: true,
        attributes: false,
        subtree: false // Only direct children to avoid conflicts with Appipa's observers
      });
    }
    
    // Also add blur listener as backup
    input.addEventListener('blur', () => {
      // Check after a short delay to let Appipa's handlers complete
      setTimeout(() => {
        const wrapper = input.closest('.quantity-selector');
        if (wrapper) {
          const hasMinus = wrapper.querySelector('button.quantity-minus');
          const hasPlus = wrapper.querySelector('button.quantity-plus');
          if (!hasMinus || !hasPlus) {
            debouncedRestore();
          }
        } else {
          debouncedRestore();
        }
      }, 100);
    }, { once: false });
    
    // Store observer reference on input for cleanup if needed
    input._appipaObserver = observer;

    // Mark as transforming to prevent duplicate processing
    input.dataset.appipaTransformed = 'true';
    
    // Save references to siblings BEFORE moving the input
    const nextSibling = input.nextSibling;
    
    // Assemble the quantity selector - this moves input out of its parent
    quantitySelector.appendChild(minusButton);
    quantitySelector.appendChild(input);
    quantitySelector.appendChild(plusButton);

    // Insert the quantity selector where the input was
    // Use the saved nextSibling reference since input is now moved
    try {
      if (parent && parent.isConnected) {
        if (nextSibling && parent.contains(nextSibling)) {
          // Insert before the next sibling (where input was)
          parent.insertBefore(quantitySelector, nextSibling);
        } else {
          // No next sibling, append to end
          parent.appendChild(quantitySelector);
        }
      } else {
        // Parent was disconnected, try to find calculator
        const calculator = document.getElementById("calculator");
        if (calculator && calculator.isConnected) {
          calculator.appendChild(quantitySelector);
        }
      }
    } catch (error) {
      // Fallback: try to append to calculator
      const calculator = document.getElementById("calculator");
      if (calculator && calculator.isConnected) {
        calculator.appendChild(quantitySelector);
      }
    }

    // Initial button state update
    updateButtonStates();
    
  });
}

/**
 * Hides parent .element wrappers for select fields that only have a single option
 * Only adds the appipa-hidden class to the parent wrapper - no other DOM changes
 */
function hideSingleOptionSelects() {
  const calculator = document.getElementById("calculator");
  if (!calculator) {
    return;
  }

  // Find all select fields in the calculator
  const selects = calculator.querySelectorAll('select');
  
  selects.forEach(select => {
    // Skip if already processed (check parent wrapper)
    const parentElement = select.closest('.element');
    if (parentElement && parentElement.classList.contains('appipa-hidden')) {
      return;
    }
    
    // Count valid options (excluding empty or disabled placeholder options)
    const selectElement = /** @type {HTMLSelectElement} */ (select);
    const options = Array.from(selectElement.options).filter(option => {
      return option.value && !option.disabled && option.text.trim() !== '';
    });
    
    // If only one valid option, hide the parent .element wrapper
    if (options.length === 1 && parentElement) {
      parentElement.classList.add('appipa-hidden');
    }
  });
}

/**
 * Hides parent .element wrappers for select fields that only have a single option
 * Only adds the appipa-hidden class to the parent wrapper - no other DOM changes
 */
function hideSingleOptionSelects() {
  const calculator = document.getElementById("calculator");
  if (!calculator) {
    return;
  }

  // Find all select fields in the calculator
  const selects = calculator.querySelectorAll('select');
  
  selects.forEach(select => {
    // Skip if parent wrapper is already hidden
    const parentElement = select.closest('.element');
    if (parentElement && parentElement.classList.contains('appipa-hidden')) {
      return;
    }
    
    // Count valid options (excluding empty or disabled placeholder options)
    const selectElement = /** @type {HTMLSelectElement} */ (select);
    const options = Array.from(selectElement.options).filter(option => {
      return option.value && !option.disabled && option.text.trim() !== '';
    });
    
    // If only one valid option, hide the parent .element wrapper
    if (options.length === 1 && parentElement) {
      parentElement.classList.add('appipa-hidden');
    }
  });
}

/**
 * Shows the calculator when user selects "custom sizing"
 */
function showCalculator() {
  const calculatorContainer = document.querySelector('.cpc-calculator-target') || 
                              document.getElementById('calculator-block');
  
  const calculator = document.getElementById("calculator");
  
  if (calculatorContainer) {
    calculatorContainer.style.display = '';
    calculatorContainer.classList.remove('appipa-calculator-hidden');
  }
  
  if (calculator) {
    calculator.style.display = '';
    calculator.classList.remove('appipa-calculator-hidden');
    
    // Re-enable calculator inputs that were disabled
    const calculatorInputs = calculator.querySelectorAll('[data-appipa-disabled="true"]');
    calculatorInputs.forEach(input => {
      input.disabled = false;
      input.removeAttribute('data-appipa-disabled');
    });

    // Transform number inputs to match quantity selector styling
    transformCalculatorNumberInputs();
    
    // Hide single-option select dropdowns
    hideSingleOptionSelects();

    // Set up observer to watch for new inputs being added dynamically
    setupCalculatorInputObserver();
    
    // Set up observer to watch for price changes and update add-to-cart button
    setupPriceTickerObserver();
  }

  // Re-enable calculator hidden inputs
  const calculatorHiddenInputs = document.querySelectorAll(
    '#variantIdCustom[data-appipa-disabled="true"], #cpcPriceToken[data-appipa-disabled="true"], #calculation_inp_Price_Each_Inc_VAT[data-appipa-disabled="true"]'
  );
  calculatorHiddenInputs.forEach(input => {
    input.disabled = false;
    input.removeAttribute('data-appipa-disabled');
  });

  // Hide product form elements when calculator is shown
  updateProductFormElementsVisibility();
}

/**
 * Hides the calculator when user deselects "custom sizing"
 * Also restores default add-to-cart button and variant selector behavior
 */
function hideCalculatorAndRestoreDefaults() {
  // Hide calculator
  hideCalculator();

  // Show product form elements when calculator is hidden
  updateProductFormElementsVisibility();

  // Disable all calculator inputs so they don't get submitted with the form
  const calculator = document.getElementById("calculator");
  if (calculator) {
    const calculatorInputs = calculator.querySelectorAll('input, select, textarea');
    calculatorInputs.forEach(input => {
      input.disabled = true;
      input.setAttribute('data-appipa-disabled', 'true');
    });
  }

  // Remove calculator-specific hidden inputs that shouldn't be submitted
  const calculatorHiddenInputs = document.querySelectorAll(
    '#variantIdCustom, #cpcPriceToken, #calculation_inp_Price_Each_Inc_VAT'
  );
  calculatorHiddenInputs.forEach(input => {
    input.disabled = true;
    input.setAttribute('data-appipa-disabled', 'true');
  });

  // Restore variant selector names (reverse what internalRenderCalculator does)
  // The ProcessProductPage renamed "id" to "oldId" - we need to restore the original "id" field
  const productForm = document.querySelector('form[action*="/cart/add"]');
  if (productForm) {
    // Find the oldId input (created by ProcessProductPage)
    const oldIdInput = productForm.querySelector('input[name="oldId"], select[name="oldId"]');
    if (oldIdInput) {
      const variantIdValue = oldIdInput.value;
      
      // Find the correct variant ID input (the one with ref="variantId")
      const variantIdInput = productForm.querySelector('input[ref="variantId"]');
      if (variantIdInput) {
        // Update the variant ID input with the correct value
        variantIdInput.value = variantIdValue;
        variantIdInput.name = 'id';
        
        // Ensure it's not disabled
        variantIdInput.disabled = false;
      } else {
        // If no ref="variantId" input exists, restore the oldId input
        oldIdInput.setAttribute("name", "id");
      }
      
      // Remove or disable the oldId input to avoid duplicates
      // But keep it if it's the only one with the variant ID
      const allIdInputs = productForm.querySelectorAll('input[name="id"], input[name="oldId"]');
      allIdInputs.forEach((input) => {
        if (input.name === 'oldId' && variantIdInput) {
          // Remove oldId if we have the variantId ref input
          input.remove();
        } else if (input.name === 'id' && input.id === 'variantIdCustom') {
          // Disable the calculator's variant ID input
          input.disabled = true;
        } else if (input.name === 'id' && input.value === '0') {
          // Remove or fix any "id" inputs with value 0 (from calculator)
          if (variantIdInput) {
            input.remove();
          } else {
            input.value = variantIdValue;
          }
        }
      });
    }
  }

  // Restore price elements visibility (reverse what internalRenderCalculator does)
  const priceElements = [
    "product-price",
    "product-single__price",
    "selector-wrapper",
    "current_price",
    "product__price",
    "shopify-payment-button",
    "single-option-selector",
  ];
  
  priceElements.forEach((elem) => {
    if (window.jQuery) {
      const elementToShow = window.jQuery("." + elem);
      elementToShow.removeClass("hide-element");
    } else {
      const elements = document.querySelectorAll("." + elem);
      elements.forEach(el => el.classList.remove("hide-element"));
    }
  });
}

/**
 * Checks if calculator is currently rendered
 */
function isCalculatorRendered() {
  const calculator = document.getElementById("calculator");
  const calculatorContainer = document.querySelector('.cpc-calculator-target') || 
                              document.getElementById('calculator-block');
  
  const isVisible = calculator !== null && 
                   calculator.style.display !== 'none' && 
                   !calculator.classList.contains('appipa-calculator-hidden');
  
  const isContainerVisible = calculatorContainer !== null && 
                             calculatorContainer.style.display !== 'none' && 
                             !calculatorContainer.classList.contains('appipa-calculator-hidden');
  
  return isVisible || isContainerVisible;
}

/**
 * Updates product form elements visibility based on calculator state
 */
function updateProductFormElementsVisibility() {
  const isVisible = isCalculatorRendered();
  const quantityRows = document.querySelectorAll('.product-form-quantity-row');
  const pricingWrappers = document.querySelectorAll('.pricing-wrapper');
  
  if (isVisible) {
    // Calculator is visible - hide elements
    quantityRows.forEach(row => {
      row.classList.add('appipa-hidden');
    });
    pricingWrappers.forEach(wrapper => {
      wrapper.classList.add('appipa-hidden');
    });
  } else {
    // Calculator is hidden - show elements
    quantityRows.forEach(row => {
      row.classList.remove('appipa-hidden');
    });
    pricingWrappers.forEach(wrapper => {
      wrapper.classList.remove('appipa-hidden');
    });
  }
}

/**
 * Sets up a simple observer to watch for product form elements being added to DOM
 * and applies/removes the appipa-hidden class based on calculator visibility
 */
let productFormElementsObserver = null;

function setupProductFormElementsObserver() {
  // Disconnect existing observer if any
  if (productFormElementsObserver) {
    productFormElementsObserver.disconnect();
    productFormElementsObserver = null;
  }

  // Find the product form or product details section to observe
  const productForm = document.querySelector('form[action*="/cart/add"]');
  const productDetails = document.querySelector('.product-details') || 
                          document.querySelector('.product-information') ||
                          document.querySelector('.shopify-section[data-section-type="product-information"]');
  
  const observeTarget = productForm || productDetails || document.body;
  if (!observeTarget) return;

  productFormElementsObserver = new MutationObserver(() => {
    // Simply update visibility based on current calculator state
    updateProductFormElementsVisibility();
  });

  // Start observing
  productFormElementsObserver.observe(observeTarget, {
    childList: true,
    subtree: true
  });
}

// Set up observer when DOM is ready
if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setupProductFormElementsObserver();
    });
  } else {
    setupProductFormElementsObserver();
  }
}

/**
 * Waits for calculator to be rendered by ProcessProductPage and hides it
 * Uses MutationObserver to watch for calculator DOM insertion
 */
function waitForAndHideCalculator() {
  return new Promise((resolve) => {
    // Check if calculator already exists
    const existingCalculator = document.getElementById("calculator");
    if (existingCalculator) {
      // Transform number inputs before hiding
      transformCalculatorNumberInputs();
      // Set up observer for future inputs (will be stopped when hidden)
      setupCalculatorInputObserver();
      hideCalculator();
      resolve(true);
      return;
    }

    // Also check for calculator containers
    const container = document.querySelector('.cpc-calculator-target') || 
                      document.getElementById('calculator-block');
    if (container && container.querySelector('#calculator')) {
      // Transform number inputs before hiding
      transformCalculatorNumberInputs();
      // Set up observer for future inputs (will be stopped when hidden)
      setupCalculatorInputObserver();
      hideCalculator();
      resolve(true);
      return;
    }

    let found = false;

    // Watch for calculator to be added to DOM
    const observer = new MutationObserver((mutations, obs) => {
      if (found) return; // Already found and processed
      
      const calculator = document.getElementById("calculator");
      if (calculator) {
        found = true;
        // Transform number inputs before hiding
        transformCalculatorNumberInputs();
        // Set up observer for future inputs (will be stopped when hidden)
        setupCalculatorInputObserver();
        hideCalculator();
        obs.disconnect();
        resolve(true);
        return;
      }

      // Also check containers
      const container = document.querySelector('.cpc-calculator-target') || 
                        document.getElementById('calculator-block');
      if (container && container.querySelector('#calculator')) {
        found = true;
        // Transform number inputs before hiding
        transformCalculatorNumberInputs();
        // Set up observer for future inputs (will be stopped when hidden)
        setupCalculatorInputObserver();
        hideCalculator();
        obs.disconnect();
        resolve(true);
        return;
      }
    });

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Timeout after 15 seconds (increased from 10) - calculators can take time to load
    setTimeout(() => {
      if (!found) {
        observer.disconnect();
      }
      resolve(found);
    }, 15000);
  });
}

/**
 * Watches the Appipa calculator price ticker and updates add-to-cart button text
 */
let priceTickerObserver = null;

/**
 * Sets up a MutationObserver to watch for price changes in the Appipa calculator
 * and update the add-to-cart button text accordingly
 */
function setupPriceTickerObserver() {
  // Disconnect existing observer if any
  if (priceTickerObserver) {
    priceTickerObserver.disconnect();
    priceTickerObserver = null;
  }

  const priceTicker = document.getElementById('priceTicker');
  if (!priceTicker) {
    return;
  }

  // Function to update add-to-cart button text
  const updateAddToCartText = () => {
    const priceValue = priceTicker.textContent?.trim() || priceTicker.innerText?.trim() || '';
    if (!priceValue) return;

    // Find all add-to-cart buttons within product forms
    const productForms = document.querySelectorAll('product-form-component');
    productForms.forEach(form => {
      const addToCartTextContent = form.querySelector('.add-to-cart-text__content span span');
      if (addToCartTextContent) {
        // Update text to "Add to cart - Add to cart - £[APPIPA VALUE]" as per user request
        addToCartTextContent.textContent = `Add to cart - £${priceValue}`;
      }
    });
  };

  // Initial update
  updateAddToCartText();

  // Set up observer to watch for text changes
  priceTickerObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'childList' || mutation.type === 'characterData') {
        updateAddToCartText();
        break;
      }
    }
  });

  // Observe the priceTicker element for text changes
  priceTickerObserver.observe(priceTicker, {
    childList: true,
    subtree: true,
    characterData: true
  });
}

/**
 * Stops watching the price ticker
 */
function stopPriceTickerObserver() {
  if (priceTickerObserver) {
    priceTickerObserver.disconnect();
    priceTickerObserver = null;
  }
}

// Expose globally for easier access
if (typeof window !== 'undefined') {
  window.AppipaCalculatorManager = {
    hideCalculator,
    showCalculator,
    hideCalculatorAndRestoreDefaults,
    isCalculatorRendered,
    waitForAndHideCalculator,
    transformCalculatorNumberInputs,
    updateProductFormElementsVisibility,
    setupPriceTickerObserver,
    stopPriceTickerObserver
  };
  
  // Check if calculator already exists when script loads, and also watch for it to be created
  const checkAndTransformCalculator = () => {
    const calculator = document.getElementById("calculator");
    const container = document.querySelector('.cpc-calculator-target') || 
                      document.getElementById('calculator-block');
    
    if (calculator || container) {
      const calculatorElement = calculator || (container && container.querySelector('#calculator'));
      
      if (calculatorElement) {
        const isVisible = (calculatorElement.style.display !== 'none' && 
                          !calculatorElement.classList.contains('appipa-calculator-hidden')) ||
                          // Also check if container is visible
                          (container && container.style.display !== 'none' && 
                           !container.classList.contains('appipa-calculator-hidden'));
        
        if (isVisible) {
          transformCalculatorNumberInputs();
          hideSingleOptionSelects();
          setupCalculatorInputObserver();
          setupPriceTickerObserver();
        } else {
          setupCalculatorInputObserver();
        }
        return true;
      }
    }
    
    return false;
  };
  
  // Check immediately
  setTimeout(checkAndTransformCalculator, 100);
  
  // Also watch for calculator to be created (since it's rendered asynchronously)
  const calculatorCreationObserver = new MutationObserver(() => {
    if (checkAndTransformCalculator()) {
      // Calculator found, can stop observing (but input observer will continue)
      calculatorCreationObserver.disconnect();
    }
  });
  
  calculatorCreationObserver.observe(document.body, {
    childList: true,
    subtree: true
  });
  
  // Stop observing after 10 seconds to avoid memory leaks
  setTimeout(() => {
    calculatorCreationObserver.disconnect();
  }, 10000);
  
  // Also listen for Appipa's custom events or DOM mutations that indicate calculator is ready
  // Check periodically for calculator visibility changes
  let lastCheck = 0;
  const visibilityCheckInterval = setInterval(() => {
    const now = Date.now();
    if (now - lastCheck < 500) return; // Throttle to every 500ms
    lastCheck = now;
    
    const calculator = document.getElementById("calculator");
    if (calculator && calculator.hasAttribute('data-calculator-ready')) {
      transformCalculatorNumberInputs();
      hideSingleOptionSelects();
      setupCalculatorInputObserver();
      setupPriceTickerObserver();
      clearInterval(visibilityCheckInterval);
    } else if (calculator) {
      // Check if calculator just became visible
      const isVisible = calculator.style.display !== 'none' && 
                       !calculator.classList.contains('appipa-calculator-hidden');
      const container = document.querySelector('.cpc-calculator-target') || 
                        document.getElementById('calculator-block');
      const containerVisible = container && container.style.display !== 'none' && 
                              !container.classList.contains('appipa-calculator-hidden');
      
      if ((isVisible || containerVisible) && !calculator.hasAttribute('data-appipa-transformed-check')) {
        calculator.setAttribute('data-appipa-transformed-check', 'true');
        transformCalculatorNumberInputs();
        hideSingleOptionSelects();
        setupCalculatorInputObserver();
        setupPriceTickerObserver();
      }
    }
  }, 200); // Check every 200ms
  
  // Stop checking after 15 seconds
  setTimeout(() => {
    clearInterval(visibilityCheckInterval);
  }, 15000);
}

